import { useEffect, useMemo, useRef } from "react";

import { PanelSegmentedFilter } from "@/components/layout/SplitPanel/PanelSegmentedFilter.tsx";
import { useLocalStorage } from "@/hooks/useLocalStorage.ts";
import {
  ALL_AREAS,
  type AreaRoleItem,
  type AreaRoleSelection,
  areaPills,
  DEFAULT_AREA_ROLE_SELECTION,
  parseAreaRoleSelection,
  toggleRole,
} from "@/shared/areaRoleFilter.ts";
import { ROUTINE_ROLES, routineRoleIdSchema } from "@/types/routineAreas.ts";
import type { PanelControlAxes, PanelControlDimension } from "@/views/Settings/panelControls.tsx";

/** A page's area and role filter: the selection, and the controls that change it. */
export type AreaRoleFilter = {
  selection: AreaRoleSelection;
  setArea: (areaKey: string) => void;
  /** Back to All and every role. */
  reset: () => void;
  /** The Role group for the page's Filter menu. */
  roleAxis: PanelControlDimension;
};

/**
 * Where this browser keeps the Routines page's area and roles. Exported for
 * `clearClientState`: the selection belongs to one account, and a custom
 * area's name is that account's own content.
 */
export const ROUTINES_AREA_ROLE_STORAGE_KEY = "okthx:routines-area-role";

/** The Skills page's, apart from the Routines page's, for the same reasons. */
export const SKILLS_AREA_ROLE_STORAGE_KEY = "okthx:skills-area-role";

/** The Packs page's: its roles are the account's choice too. */
export const PACKS_AREA_ROLE_STORAGE_KEY = "okthx:packs-area-role";

/** The Connectors page's, apart from the others', for the same reasons. */
export const CONNECTORS_AREA_ROLE_STORAGE_KEY = "okthx:connectors-area-role";

const ALL_ROLES = "all";

const ROLE_OPTIONS = [
  { value: ALL_ROLES, label: "All roles" },
  ...ROUTINE_ROLES.map((role) => ({ value: role.id, label: role.name })),
];

/**
 * A page's area and role filter, kept in this browser so the page opens on the
 * last selection. The browser is the only place it is kept: okthx does not
 * store a user's roles, because a user can play several, or one for a while.
 *
 * Undefined while `enabled` is false, and the page then filters as it did
 * before areas and roles. The stored selection stays for when it comes back.
 */
export const useAreaRoleFilter = ({
  storageKey,
  enabled,
  statsigName,
}: {
  storageKey: string;
  enabled: boolean;
  /** Base analytics name for the Role group, e.g. "routines-role". */
  statsigName: string;
}): AreaRoleFilter | undefined => {
  const [stored, setStored] = useLocalStorage<unknown>(storageKey, DEFAULT_AREA_ROLE_SELECTION);
  const selection = useMemo(() => parseAreaRoleSelection(stored), [stored]);
  if (!enabled) return undefined;

  // From what storage holds now rather than from this render's selection, so
  // two changes in one event cannot drop the first.
  const update = (change: (current: AreaRoleSelection) => Partial<AreaRoleSelection>) =>
    setStored((previous: unknown) => {
      const current = parseAreaRoleSelection(previous);
      return { ...current, ...change(current) };
    });

  return {
    selection,
    setArea: (area) => update(() => ({ area })),
    reset: () => update(() => DEFAULT_AREA_ROLE_SELECTION),
    roleAxis: {
      label: "Role",
      value: selection.roles.length > 0 ? selection.roles.join(",") : ALL_ROLES,
      values: selection.roles,
      onChange: (value) => {
        if (value === ALL_ROLES) {
          update(() => ({ roles: [] }));
          return;
        }
        const role = routineRoleIdSchema.safeParse(value);
        if (role.success) update((current) => ({ roles: toggleRole(current.roles, role.data) }));
      },
      options: ROLE_OPTIONS,
      defaultValue: ALL_ROLES,
      statsigName,
    },
  };
};

/**
 * A list page's filter axes. Under routines_v2 the area pills take the row above
 * the list, so the page's kind axis (All / System / Custom) moves into the menu
 * as "Show", beside Role, and the trigger counts what the two narrow: the names
 * of several roles would not fit it. Without it, the kind axis as it was.
 */
export const areaRoleFilterAxes = (
  kindAxis: PanelControlDimension,
  areaRole: AreaRoleFilter | undefined,
): Pick<PanelControlAxes, "filters" | "countApplied"> =>
  areaRole
    ? {
        filters: [{ ...kindAxis, label: "Show", display: "menu" }, areaRole.roleAxis],
        countApplied: true,
      }
    : { filters: [kindAxis] };

/**
 * The row of area pills above a list: All, the built-in areas, then the user's
 * own, each counting `items`. The segment row of the panel toolbars, so the
 * pills read as the same family of control.
 *
 * The selected pill is scrolled into the row's view. The selection is
 * remembered, so a page can open on a pill past the row's edge, and a list
 * narrowed by a pill the user cannot see reads as missing items.
 */
export const AreaPills = ({
  items,
  labelItems,
  filter,
  statsigName,
}: {
  /** The list under every filter but the area. */
  items: readonly AreaRoleItem[];
  /** The whole list, which names the user's own areas; see `areaPills`. */
  labelItems?: readonly AreaRoleItem[];
  filter: AreaRoleFilter;
  statsigName: string;
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const selected = filter.selection.area;
  useEffect(() => {
    if (selected === ALL_AREAS) return;
    // Centered, so the pill clears the row's edge fade and arrow; "nearest"
    // vertically, so the row moves and the page does not.
    rowRef.current
      ?.querySelector("button[aria-pressed='true']")
      ?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [selected]);

  return (
    <div ref={rowRef} className="mb-4 flex">
      <PanelSegmentedFilter
        axis={{
          label: "Area",
          value: filter.selection.area,
          onChange: filter.setArea,
          options: areaPills(items, filter.selection.area, labelItems).map((pill) => ({
            value: pill.key,
            label: pill.label,
            count: pill.count,
          })),
          defaultValue: ALL_AREAS,
          display: "segmented",
          statsigName,
        }}
      />
    </div>
  );
};
